import { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { RequestCustomerApproval, regenerateFinanceDocument } from "services/customers.service";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchFinanceDetailRefreshAsync } from "containers/finance-detail/finance-detail-container.slice";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";

interface ConfirmModalProps {
  companyId: string;
  customerId: string;
  year?: number;
  shouldRegenerateDocument?: boolean;
  financeId?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  companyId,
  customerId,
  year,
  shouldRegenerateDocument,
  financeId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  // The company of the page, not of the customer: the summary shown is the page's.
  const { companyId: pageCompanyId } = useParams();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleApproval = async () => {
    try {
      setIsLoading(true);
      const formData = { ...(financeId && { financeId }) };
      await RequestCustomerApproval(companyId, customerId, formData, year);
      // The approval leaves the customer finance approvals queue: the bell follows.
      if (pageCompanyId) dispatch(fetchCabinetSummaryThunk(pageCompanyId));
      // isEdit: silent background refetch — keeps the finance page's shared
      // customer data visible instead of flipping it to the blocking loader.
      dispatch(fetchCustomer({ customerId, companyId: companyId!, isEdit: true }));

      // If document exists and is not signed, regenerate it with current finance data
      if (shouldRegenerateDocument && year) {
        await regenerateFinanceDocument(companyId, customerId, year, financeId ?? undefined);
      }

      // Refresh finance detail if year is provided (pass financeId to stay on same revision)
      if (year) {
        dispatch(fetchFinanceDetailRefreshAsync({
          companyId,
          customerId,
          year,
          financeId,
        }));
      }

      setIsLoading(false);
      closeModal();
    } catch (e) {
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("Are_you_sure_you_want_to_approve_customer")}
      </h1>
      <InlineStack align="space-between">
        <Button onClick={handleApproval} loading={isLoading}>
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
